<template>
  <div class="card">
    <FileUpload
      :multiple="false"
      accept="text/*, .pdf, .doc, .docx, .jpeg, .jpg, .png"
      @select="onSelectedFiles"
    >
      <template
        #header="{ chooseCallback }"
      >
        <div
          class="flex flex-wrap justify-content-between align-items-center flex-1 gap-2"
        >
          <div class="flex gap-2">
            <Button
              icon="pi pi-file"
              rounded
              outlined
              @click="chooseCallback()"
            />
          </div>
        </div>
      </template>
      <template
        #content="{
          files,
        }"
      >
        <div v-if="files.length > 0">
          <h5>Completed</h5>
        </div>
      </template>
      <template #empty>
        <div class="flex align-items-center justify-content-center flex-column alignment">
          <i
            class="pi pi-cloud-upload border-2 border-circle p-5 text-8xl text-400 border-400"
          />
          <p class="mt-4 mb-0">
            Drag and drop files to here to upload.
          </p>
        </div>
      </template>
    </FileUpload>
  </div>
</template>

<script>
import FileUpload from "primevue/fileupload";
import Button from "primevue/button";

import 'primeicons/primeicons.css';


import { useSocketCommunication } from "./composables/useSocketCommunication";

import { MAX_PDF_PAGES, DAILY_LIMIT_EXCEEDED_STATUS } from "@/assets/constants/constants";

export default {
    components: {
        FileUpload,
        Button,
    },
    props: {
        subjectId: {
            type: String,
            default: null
        },
    },
    emits: ['show-toast'],
    data() {
        return {
        files: [],
        totalSize: 0,
        totalSizePercent: 0,
        pdfjsLoaded: false,
        socketCommunication: null,
        chunkSize: 512 * 1024, // 512KB chunks to stay under WebSocket limits
        };
    },
    methods: {
        onSelectedFiles(event) {
            this.files = event.files;
            this.files.forEach((file) => {
                this.totalSize += parseInt(this.formatSize(file.size));
            });
            this.uploadDocuments(this.files);
        },
        async loadPdfJs() {
            if (this.pdfjsLoaded) return;
            
            return new Promise((resolve, reject) => {
                const script = document.createElement('script');
                script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
                script.onload = () => {
                    if (window.pdfjsLib) {
                        window.pdfjsLib.GlobalWorkerOptions.workerSrc = 
                            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';
                        this.pdfjsLoaded = true;
                        resolve();
                    } else {
                        reject(new Error('PDF.js library failed to load'));
                    }
                };
                script.onerror = () => reject(new Error('Failed to load PDF.js library'));
                document.head.appendChild(script);
            });
        },
        async getPdfPageCount(file) {
            try {
                await this.loadPdfJs();
                const arrayBuffer = await file.arrayBuffer();
                const pdf = await window.pdfjsLib.getDocument(arrayBuffer).promise;
                return pdf.numPages;
            } catch (error) {
                console.error("Error reading PDF:", error);
                throw new Error("Unable to read PDF file");
            }
        },
        setupUploadResponseHandler() {
            this.socketCommunication.setupSocketHandlers(
                (messageInfo) => {
                    // Handle upload response messages
                    if (messageInfo.upload_status === DAILY_LIMIT_EXCEEDED_STATUS) {
                        this.$emit('show-toast', {
                            severity: 'error',
                            summary: 'Daily Limit Reached',
                            detail: 'You have reached your daily upload limit for scanned documents. Please try again tomorrow or upload a different type of document.',
                            life: 5000
                        });
                    }
                },
                (error) => {
                    console.error('WebSocket error:', error);
                },
                () => {
                    console.log('WebSocket connection closed');
                }
            );
        },
        formatSize(bytes) {
            const k = 1024;
            const dm = 3;
            const sizes = this.$primevue.config.locale.fileSizeTypes;

            if (bytes === 0) {
                return `0 ${sizes[0]}`;
            }

            const i = Math.floor(Math.log(bytes) / Math.log(k));
            const formattedSize = parseFloat((bytes / Math.pow(k, i)).toFixed(dm));

            return `${formattedSize} ${sizes[i]}`;
        },
        async uploadDocuments(upFiles) {            
            // Initialize WebSocket communication with authentication
            if (!this.socketCommunication) {
                this.socketCommunication = useSocketCommunication();
            }
            
            const authToken = localStorage.getItem('authToken');
            this.socketCommunication.initializeSocket(authToken);
            
            // Setup socket message handlers for upload responses
            this.setupUploadResponseHandler();
            
            const uploadPromises = [];
            let completedUploads = 0;
            const totalUploads = upFiles.length;
            
            for (let i = 0; i < upFiles.length; i++) {
                const fileExt = upFiles[i].name.split(".").pop();
                
                // Check PDF page limit
                if (fileExt.toLowerCase() === "pdf") {
                    try {
                        const pageCount = await this.getPdfPageCount(upFiles[i]);
                        if (pageCount > MAX_PDF_PAGES) {
                            this.$emit('show-toast', {
                                severity: 'error',
                                summary: 'PDF Page Limit',
                                detail: `We apologize, but the current beta version only supports PDF files with up to ${MAX_PDF_PAGES} pages. This file has ${pageCount} pages.`,
                                life: 5000
                            });
                            continue;
                        }
                    } catch (error) {
                        this.$emit('show-toast', {
                            severity: 'error',
                            summary: 'PDF Read Error',
                            detail: error.message,
                            life: 5000
                        });
                        continue;
                    }
                }
                
                uploadPromises.push(this.uploadFileViaWebSocket(upFiles[i], fileExt, () => {
                    completedUploads++;
                }));
            }
        },
        
        generateFileId(file) {
            // Create unique ID from file name, size, and timestamp
            return `${file.name}-${file.size}-${Date.now()}`;
        },
        
        uploadFileViaWebSocket(file, fileExt, onUploadCompleted) {
            return new Promise((resolve) => {
                const fileId = this.generateFileId(file);
                const totalChunks = Math.ceil(file.size / this.chunkSize);
                let currentChunk = 0;
                
                const readAndSendChunk = () => {
                    if (currentChunk >= totalChunks) {
                        // All chunks sent
                        onUploadCompleted();
                        resolve();
                        return;
                    }
                    
                    const start = currentChunk * this.chunkSize;
                    const end = Math.min(start + this.chunkSize, file.size);
                    const chunk = file.slice(start, end);
                    
                    const reader = new FileReader();
                    
                    reader.onload = (event) => {
                        // Extract base64 from data URL (remove "data:...;base64," prefix)
                        const base64Chunk = event.target.result.split(',')[1];
                        
                        // Wait for socket to open before sending
                        const checkSocket = setInterval(() => {
                            if (this.socketCommunication.isSocketOpen()) {
                                clearInterval(checkSocket);
                                
                                // Prepare chunked upload request
                                const uploadRequest = {
                                    request_type: 'file_upload_chunk',
                                    file_id: fileId,
                                    chunk_number: currentChunk,
                                    total_chunks: totalChunks,
                                    chunk_data: base64Chunk,
                                    file_name: file.name,
                                    resource_type: fileExt,
                                    subject_id: this.subjectId,
                                };
                                
                                this.socketCommunication.sendUploadRequest(uploadRequest);
                                currentChunk++;
                                readAndSendChunk();
                            }
                        }, 100);
                        
                        // Timeout after 5 seconds
                        setTimeout(() => {
                            clearInterval(checkSocket);
                            if (currentChunk === 0) {
                                this.$emit('show-toast', {
                                    severity: 'error',
                                    summary: 'Upload Timeout',
                                    detail: 'Failed to connect for file upload.',
                                    life: 5000
                                });
                                resolve();
                            }
                        }, 5000);
                    };
                    
                    reader.onerror = () => {
                        this.$emit('show-toast', {
                            severity: 'error',
                            summary: 'File Read Error',
                            detail: 'Unable to read file.',
                            life: 5000
                        });
                        resolve();
                    };
                    
                    reader.readAsDataURL(chunk);
                };
                
                readAndSendChunk();
            });
        },
    },
};
</script>

<style scoped>
.alignment {
  text-align: center;
}

.p-progress-bar {
  background-color: white;
}
</style>
